import React from 'react';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { SectionSidebarMenu } from 'src/modules/shared/components/molecules/section-menu';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  WEBSITE_SETTINGS_STEP_LABEL_KEYS,
  WEBSITE_SETTINGS_STEPS,
  type WebsiteSettingsStep,
} from '../../utils/website-settings-steps.util';

/** Props for the website settings section navigation. */
interface WebsiteSettingsStepsProps {
  /** Section whose panel is visible. */
  activeStep: WebsiteSettingsStep;
  /** Called when the user opens a section. */
  onStepChange: (step: WebsiteSettingsStep) => void;
}

/** Matches the `lg` breakpoint where settings pages show a side menu. */
const SIDE_MENU_MEDIA_QUERY = '(min-width: 1024px)';

/**
 * Section navigation in the app's settings style: the shared side menu on
 * wide screens, and the same sections as one swipeable row of tabs above the
 * content on narrow ones.
 *
 * @param props - Active section and the change callback.
 * @returns The section navigation.
 */
export const WebsiteSettingsSteps: React.FC<WebsiteSettingsStepsProps> = ({
  activeStep,
  onStepChange,
}) => {
  const { t } = useI18n();
  const showSideMenu = useMediaQuery(SIDE_MENU_MEDIA_QUERY);
  const items = WEBSITE_SETTINGS_STEPS.map((step) => ({
    id: step,
    label: t(WEBSITE_SETTINGS_STEP_LABEL_KEYS[step]),
  }));
  const selectStep = (id: string) => {
    const step = WEBSITE_SETTINGS_STEPS.find((candidate) => candidate === id);
    if (step) onStepChange(step);
  };

  return (
    <nav aria-label={t('websites.settings.steps.label')}>
      {showSideMenu ? (
        <SectionSidebarMenu
          items={items}
          activeId={activeStep}
          onSelect={selectStep}
          variant="neutral"
          appearance="soft"
          contentClassName="mt-0"
        />
      ) : (
        <Tabs
          value={activeStep}
          onChange={selectStep}
          variant="neutral"
          appearance="soft"
          size="sm"
          scrollable
          align="left"
        >
          {items.map((item) => (
            <Tab key={item.id} value={item.id} label={item.label} />
          ))}
        </Tabs>
      )}
    </nav>
  );
};
